import React from 'react';
import { FolderOpen, X } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useProjectStore } from '../../store/projectStore';

type TabType = 'design';

interface TabBarProps {
  type: TabType;
  onCloseConfirm: (id: string, name: string) => void;
  onListClick?: () => void;
  isListOpen?: boolean;
}

const TabBar: React.FC<TabBarProps> = ({ type, onCloseConfirm, onListClick, isListOpen }) => {
  const { 
    activeDesignId, 
    setActiveDesignTab, 
    getOpenDesigns,
    openDesigns,
  } = useProjectStore(
    useShallow((s) => ({
      activeDesignId: s.activeDesignId,
      setActiveDesignTab: s.setActiveDesignTab,
      getOpenDesigns: s.getOpenDesigns,
      openDesigns: s.openDesigns,
    }))
  );
  void openDesigns;
  
  const openItems = getOpenDesigns();
  const activeId = activeDesignId;
  const setActiveTab = setActiveDesignTab;

  const showDesignListButton = type === 'design' && onListClick;

  if (openItems.length === 0 && !showDesignListButton) {
    return null;
  }

  // Tabs divide the remaining space equally (Affinity Designer behavior)
  const tabWidth = openItems.length > 0 ? `calc(100% / ${openItems.length})` : undefined;

  const handleTabClick = (id: string) => {
    setActiveTab(id);
  };

  const handleCloseClick = (e: React.MouseEvent, id: string, name: string) => {
    e.stopPropagation();
    onCloseConfirm(id, name);
  };

  return (
    <div
      className="bg-slate-800 border-b border-slate-700 flex items-center ml-12"
      style={{ minHeight: '28px', maxHeight: '28px' }}
      data-tab-bar="true"
    >
      {showDesignListButton && (
        <button
          type="button"
          data-list-toggle-button="true"
          onClick={(e) => {
            e.stopPropagation();
            onListClick();
          }}
          title="Open Design"
          aria-label="Open Design"
          className={`
            flex-shrink-0 w-6 h-6 ml-2 mr-2 flex items-center justify-center rounded transition-colors
            ${isListOpen
              ? 'bg-slate-700 text-white'
              : 'text-slate-400 hover:text-white hover:bg-slate-750'
            }
          `}
        >
          <FolderOpen size={13} />
        </button>
      )}
      <div className="flex flex-1 min-w-0 items-center">
      {openItems.map((item: any, index: number) => {
        const isActive = activeId === item.id;
        const isFirstTab = index === 0;
        const itemName = item.name || 'Untitled Design';
        return (
          <div
            key={item.id}
            onClick={() => handleTabClick(item.id)}
            className={`
              flex items-center justify-between py-1 cursor-pointer transition-colors
              ${isActive 
                ? 'bg-slate-700 text-white' 
                : 'bg-slate-800 hover:bg-slate-750 text-slate-300 hover:text-white'
              }
            `}
            style={{ 
              width: tabWidth,
              flexShrink: 0,
              paddingLeft: isFirstTab ? '4px' : '8px',
              paddingRight: '8px'
            }}
          >
            <span className="text-xs font-medium truncate flex-1 mr-2" title={itemName}>
              {itemName}
            </span>
            <button
              onClick={(e) => handleCloseClick(e, item.id, itemName)}
              className={`
                flex-shrink-0 p-0.5 rounded hover:bg-slate-600 transition-colors
                ${isActive ? 'text-slate-300 hover:text-white' : 'text-slate-400 hover:text-slate-200'}
              `}
              title="Close tab"
            >
              <X size={12} />
            </button>
          </div>
        );
      })}
      </div>
    </div>
  );
};

export default TabBar;
